/* @flow */
import Vue from 'vue'
import axios from 'axios'
import normalize from 'jsonapi-normalizer'
import oPath from 'object-path'

import {REMOVE_SESSION} from './session'
import {NEW_NOTIFICATION, REPLACE_NOTIFICATION} from './notifications'
import type {JsonApiEntity} from '@/store'

export const REPLACE_DEVICE_PROFILES = 'REPLACE_DEVICE_PROFILES'
export const ADD_DEVICE_PROFILES = 'ADD_DEVICE_PROFILES'
export const MODIFY_DEVICE_PROFILE = 'MODIFY_DEVICE_PROFILE'
export const REMOVE_DEVICE_PROFILE = 'REMOVE_DEVICE_PROFILE'
export const START_DEVICE_PROFILE_FETCHING = 'START_DEVICE_PROFILE_FETCHING'
export const DONE_DEVICE_PROFILE_FETCHING = 'DONE_DEVICE_PROFILE_FETCHING'
export const SET_DEVICE_PROFILES_TOTAL = 'SET_DEVICE_PROFILES_TOTAL'

export const FETCH_DEVICE_PROFILES = 'FETCH_DEVICE_PROFILES'
export const FETCH_DEVICE_PROFILE = 'FETCH_DEVICE_PROFILE'
export const CREATE_DEVICE_PROFILE = 'CREATE_DEVICE_PROFILE'
export const UPDATE_DEVICE_PROFILE = 'UPDATE_DEVICE_PROFILE'
export const DELETE_DEVICE_PROFILE = 'DELETE_DEVICE_PROFILE'

const initialState = () => ({
  entities: {},
  statistics: {
    total: null,
  },
  fetch_status: {
    all: '',
  },
})

export default {
  state: initialState(),
  getters: {
    deviceProfileDefaults: () => ({
      device_profile_id: '',
      supports_class_b: false,
      supports_class_c: false,
      mac_version: '1.0.2',
      reg_params_revision: 'B',
      max_eirp: 10,
      supports_join: true,
      rf_region: 'US915',
      supports_32bit_f_cnt: true,
    }),
    deviceProfiles: ({entities = {}}: Object) =>
      //$FlowFixMe
      Object.keys(entities).map(key => entities[key]),
    // Returns an object with all device-profiles, keyed by device-profile EUI
    deviceProfilesKeyedById: (state: Object, getters: Object) => {
      const deviceProfiles = {}
      getters.deviceProfiles.forEach(dp => {
        deviceProfiles[dp.id] = dp
      })
      return deviceProfiles
    },
    deviceProfilesById: (state: Object) => (id: string) => state.entities[id] || {},
  },
  mutations: {
    RESET_STATE(state: Object) {
      Object.assign(state, initialState())
    },
    [REMOVE_SESSION](state: Object) {
      state.entities = initialState().entities
    },
    [START_DEVICE_PROFILE_FETCHING](state: Object, payload: Object) {
      state.fetch_status[payload.key] = 'loading'
    },
    [DONE_DEVICE_PROFILE_FETCHING](state: Object, payload: Object) {
      state.fetch_status[payload.key] = 'loaded'
    },
    [REPLACE_DEVICE_PROFILES](state: Object, {entities = {}}: Object) {
      state.entities = entities
    },
    [ADD_DEVICE_PROFILES](state: Object, payload: Object) {
      state.entities = {
        ...state.entities,
        ...payload.entities,
      }
    },
    [MODIFY_DEVICE_PROFILE](state: Object, payload: Object) {
      state.entities[payload.id] = {
        ...state.entities[payload.id],
        ...payload,
      }
    },
    [REMOVE_DEVICE_PROFILE](state: Object, payload: Object) {
      Vue.delete(state.entities, payload.id)
    },
    [SET_DEVICE_PROFILES_TOTAL](state: Object, payload: Object) {
      state.statistics.total = payload.total
    },
  },
  actions: {
    [FETCH_DEVICE_PROFILES]: async function({commit, dispatch}: Object, payload: Object) {
      let s1 = null
      try {
        commit(START_DEVICE_PROFILE_FETCHING, {key: 'all'})
        if (payload && payload.organization_id) {
          s1 = `/organizations/${payload.organization_id}/device_profiles`
        } else {
          s1 = `/device_profiles`
        }

        const {data} = await axios.get(s1, payload)
        commit(SET_DEVICE_PROFILES_TOTAL, {total: data.meta.pagination.collection_count})

        const entities = {}
        data.data.map(dp => {
          entities[dp.id] = {
            ...dp.attributes,
            ...dp.meta,
          }
        })

        commit(REPLACE_DEVICE_PROFILES, {
          entities: normalize(data).entities.device_profiles,
        })

        return {
          success: true,
          // Return fetched data as flat array
        }
      } catch (err) {
        dispatch(NEW_NOTIFICATION, {
          text: 'Failed to load device-profiles ',
          detail: err.message,
          color: 'error',
        })
      } finally {
        commit(DONE_DEVICE_PROFILE_FETCHING, {key: 'all'})
      }
      return {success: false}
    },
    /*
    [FETCH_DEVICE_PROFILE]: async function({commit, dispatch}: Object, payload: Object) {
      try {
        const {data} = await axios.get(`/device_profiles/${payload.id}`)
        const {entities} = normalize(data)
        commit(ADD_DEVICE_PROFILES, {entities: entities.device_profiles})
      } catch (err) {
        dispatch(NEW_NOTIFICATION, {
          text: 'Failed to load device-profile ',
          detail: err.message,
          color: 'error',
        })
      }
    },
    [FETCH_DEVICE_PROFILE]: async function({commit, dispatch}: Object, payload: Object) {
      try {
        const {data: {data}} = await axios.get(`/device_profiles/${payload.id}`)
        commit(ADD_DEVICE_PROFILES, {
          entities: {
            [data.id]: {
              ...data.attributes,
              ...data.meta,
            },
          },
        })
        return {success: true}
      } catch (err) {
        const status = oPath.get(err, 'response.status')
        if (status !== 404) {
          dispatch(NEW_NOTIFICATION, {
            text: 'Failed to load device profile.',
            detail: err.message,
            color: 'error',
          })
        }
        return {success: false, status}
      }
    },
*/
    [FETCH_DEVICE_PROFILE]: async function({commit, dispatch}: Object, payload: Object) {
      try {
        const {data} = await axios.get(`/device_profiles/${payload.id}`)
        const {entities} = normalize(data)
        commit(ADD_DEVICE_PROFILES, {entities: entities.device_profiles})
        return {success: true}
      } catch (err) {
        const status = oPath.get(err, 'response.status')
        if (status !== 404) {
          dispatch(NEW_NOTIFICATION, {
            text: 'Failed to load device-profile ',
            detail: err.message,
            color: 'error',
          })
        }
        return {success: false, status}
      }
    },
    [CREATE_DEVICE_PROFILE]: async function(
      {commit, dispatch}: Object,
      payload: JsonApiEntity
    ) {
      try {
        const {data} = await axios.post('/device_profiles', {
          data: {
            type: 'device_profiles',
            attributes: payload.attributes,
            relationships: payload.relationships,
          },
        })
        const {entities} = normalize(data)
        commit(ADD_DEVICE_PROFILES, {entities: entities.device_profiles})
        dispatch(NEW_NOTIFICATION, {
          text: 'Saved new device-profile!',
          color: 'success',
        })
        return data.data.attributes.id
      } catch (err) {
        dispatch(NEW_NOTIFICATION, {
          text: 'Failed to save device-profile ',
          detail: err.message,
          color: 'error',
        })
      }
      return false
    },
    [UPDATE_DEVICE_PROFILE]: async function({commit, dispatch}: Object, payload: Object) {
      const notificationId = await dispatch(NEW_NOTIFICATION, {
        text: 'Saving...',
        color: 'secondary',
        undoable: true,
      })

      try {
        const {data} = await axios.patch(`/device_profiles/${payload.id}`, {
          data: {
            type: 'device_profiles',
            attributes: payload.attributes,
            relationships: payload.relationships,
          },
        })
        const {entities} = normalize(data)
        commit(MODIFY_DEVICE_PROFILE, entities.device_profiles[payload.id])
        commit(REPLACE_NOTIFICATION, {
          id: notificationId,
          text: 'Saved!',
          color: 'success',
          undoable: true,
        })
        return true
      } catch (err) {
        commit(REPLACE_NOTIFICATION, {
          id: notificationId,
          text: 'Failed to update device-profile ',
          detail: err.message,
          color: 'error',
        })
      }
      return false
    },
    [DELETE_DEVICE_PROFILE]: async function({commit, dispatch}: Object, payload: Object) {
      const notificationId = await dispatch(NEW_NOTIFICATION, {
        text: `Deleting device-profile...`,
        color: 'secondary',
      })

      try {
        await axios.delete(`/device_profiles/${payload.id}`)
        commit(REMOVE_DEVICE_PROFILE, payload)
        commit(REPLACE_NOTIFICATION, {
          id: notificationId,
          text: `Deleted device-profile!`,
          color: 'success',
        })
        return true
      } catch (err) {
        commit(REPLACE_NOTIFICATION, {
          id: notificationId,
          text: `Failed to delete device-profile `,
          detail: err.message,
          color: 'error',
        })
      }
      return false
    },
  },
}



// WEBPACK FOOTER //
// ./src/store/device_profiles.js